// KanbanBoard.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMotivosDescarte } from '~/Store/Prospecto/actions';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './styles.css';
import { ArrowsClockwise, CaretUp, CaretDown } from 'phosphor-react';
import { toast } from 'react-toastify';
import Loading from '~/Components/Loading';
import { Button, Form, Modal } from 'react-bootstrap';

function KanbanBoard({
  columns,
  cards,
  renderCard,
  onCardMove,
  acrescentarCards = null,
  isDragDisabled = false,
  disableOnSearch = false,
  regraDeFluxo,
  loadingEtapa,
  onToggleOrdenacao = null,
  ordenacaoColunas = {},
}) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [heightColunaKanban, setHeightColunaKanban] = useState();
  const [isDragging, setIsDragging] = useState(false);
  const [maiorColuna, setMaiorColuna] = useState();
  const [modalDescarte, setModalDescarte] = useState({
    openModal: false,
    result: null,
    infos: '',
  });
  const [motivoDescarte, setMotivoDescarte] = useState('');
  const [dissertativaDescarte, setDissertativaDescarte] = useState('');
  const options = useSelector(state => state.options);
  const dispatch = useDispatch();

  const containerKanbanRef = useRef(null);
  const dragState = useRef({
    startX: 0,
    scrollLeft: 0,
  });

  // Custom scrollbar refs
  const scrollbarTrackRef = useRef(null);
  const scrollbarThumbRef = useRef(null);
  const scrollbarDragRef = useRef({ isDragging: false, startX: 0, startScrollLeft: 0 });
  const scrollbarCleanupRef = useRef(null);

  const alturaPorTipoDeTela = {
    mobileExtraPequeno: 0.57,
    mobilePequeno: 0.65,
    mobileMedio: 0.67,
    grande: 0.75,
    extraGrande: 0.78,
  };

  const updateScrollbar = useCallback(() => {
    const container = containerKanbanRef.current;
    const track = scrollbarTrackRef.current;
    const thumb = scrollbarThumbRef.current;
    if (!container || !track || !thumb) return;

    const rect = container.getBoundingClientRect();
    const { scrollWidth, clientWidth, scrollLeft } = container;

    track.style.left = `${rect.left}px`;
    track.style.width = `${rect.width}px`;

    if (scrollWidth <= clientWidth) {
      track.style.display = 'none';
      return;
    }

    track.style.display = 'block';
    const trackWidth = rect.width;
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 15);
    const maxThumbLeft = trackWidth - thumbWidth;
    const maxScrollLeft = scrollWidth - clientWidth;
    const thumbLeft = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbLeft}px)`;
  }, []);

  const handleChangeEtapa = result => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      return;
    }

    if (regraDeFluxo) {
      const etapaAtual = source.droppableId;
      const etapaDestino = destination.droppableId;

      const etapasPermitidas = regraDeFluxo[etapaAtual];

      if (!etapasPermitidas.includes(parseInt(etapaDestino))) {
        const labelEtapa = id => {
          const coluna = columns.find(col => String(col.codigo) === String(id));
          return coluna.descricao || `Etapa ${id}`;
        };

        const etapaAtualLabel = labelEtapa(etapaAtual);
        const etapasPermitidasLabel = etapasPermitidas
          .map(labelEtapa)
          .join(', ');

        // Removido bloqueio especial para "Venda Realizada" (9)
        // Mostra apenas mensagem de fluxo não permitido
        toast.error(
          `Movimentação não permitida. A partir da etapa "${etapaAtualLabel}", só é possível mover para: ${etapasPermitidasLabel}.`
        );
        return;
      }
    }
    setMotivoDescarte('');
    setDissertativaDescarte('');
    if (
      options && (!options.motivosDescarte || options.motivosDescarte.length === 0)
    ) {
      dispatch(fetchMotivosDescarte());
    }
    if (String(destination.droppableId) === '10') {
      setModalDescarte({
        openModal: true,
        result,
        infos: {
          etapaDestino: destination.droppableId,
        },
      });
      return;
    }
    handleDragEnd(result);
  };

  const handleDragEnd = result => {
    const { source, destination } = result;
    const card = cards[source.droppableId][source.index];
    onCardMove &&
      onCardMove({
        source,
        destination,
        card,
        motivoDescarte: motivoDescarte || null,
        justificativa: dissertativaDescarte || null,
      });
  };

  function handleModalDescarte() {
    const destino = modalDescarte.infos && modalDescarte.infos.etapaDestino;
    if (String(destino) === '10' && !motivoDescarte) {
      toast.error('Selecione um motivo de descarte.');
      return;
    }
    setModalDescarte({ openModal: false, result: null, infos: '' });
    if (modalDescarte.result) {
      handleDragEnd(modalDescarte.result);
    } else {
      toast.error('Erro ao tentar confirmar a ação.');
    }
  }

  const handleAcrescentarCards = column => {
    acrescentarCards(column);
  };

  function obterMultiplicadorAltura(telaAltura) {
    if (telaAltura < 500) return alturaPorTipoDeTela.mobileExtraPequeno;
    if (telaAltura < 600) return alturaPorTipoDeTela.mobilePequeno;
    if (telaAltura < 700) return alturaPorTipoDeTela.mobileMedio;
    if (telaAltura < 900) return alturaPorTipoDeTela.grande;
    if (telaAltura < 1200) return alturaPorTipoDeTela.extraGrande;
    return alturaPorTipoDeTela.extraGrande;
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
      setHeightColunaKanban(obterMultiplicadorAltura(window.innerHeight));
    };

    setHeightColunaKanban(obterMultiplicadorAltura(window.innerHeight));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const columnComMaisCards = Object.entries(cards).reduce(
      (maior, [codigo, lista]) => {
        if (!maior || lista.length > cards[maior].length) {
          return codigo;
        }
        return maior;
      },
      null
    );

    setMaiorColuna((columnComMaisCards || '').toString());
  }, [cards]);

  useEffect(() => {
    const element = document.querySelector('.focar-coluna-kanban');

    if (element) {
      element.setAttribute('tabindex', '0');

      element.focus();

      const kanbanContainer = element.closest('[style*="overflowX"]');
      if (kanbanContainer) {
        const colunaContainer = element.closest('[style*="minWidth: 300"]');
        if (colunaContainer) {
          colunaContainer.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }
    }
  }, [maiorColuna]);

  // Custom scrollbar: sync on scroll, resize, and content changes
  useEffect(() => {
    const container = containerKanbanRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!scrollbarDragRef.current.isDragging) {
        requestAnimationFrame(updateScrollbar);
      }
    };

    const handleResize = () => requestAnimationFrame(updateScrollbar);

    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateScrollbar);
    });
    resizeObserver.observe(container);
    if (container.firstElementChild) {
      resizeObserver.observe(container.firstElementChild);
    }

    updateScrollbar();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      if (scrollbarCleanupRef.current) {
        scrollbarCleanupRef.current();
      }
    };
  }, [updateScrollbar]);

  useEffect(() => {
    const timer = setTimeout(updateScrollbar, 100);
    return () => clearTimeout(timer);
  }, [cards, columns, updateScrollbar]);

  const quantidadeProspectosVisiveis = () => {
    const visiveis = (columns || []).reduce((total, col) => {
      const lista = cards && cards[col.codigo] ? cards[col.codigo] : [];
      return total + ((lista || []).length || 0);
    }, 0);
    return visiveis;
  };

  const quantidadeProspectosOcultos = () => {
    const total = Object.values(cards || {}).reduce(
      (acc, lista) => acc + ((lista || []).length || 0),
      0
    );
    const visiveis = quantidadeProspectosVisiveis();
    const ocultos = total - visiveis;
    return ocultos > 0 ? ocultos : 0;
  };

  function handleMouseDown(event) {
    const target = event.target;

    if (
      target.classList.contains('kanban-card') ||
      target.closest('[data-rbd-draggable-id]') ||
      target.closest('[data-rbd-drag-handle-draggable-id]') ||
      target.closest('.kanban-card')
    ) {
      return;
    }

    setIsDragging(true);

    dragState.current.startX =
      event.pageX - containerKanbanRef.current.offsetLeft;
    dragState.current.scrollLeft = containerKanbanRef.current.scrollLeft;

    event.preventDefault();
  }

  function handleMouseMove(event) {
    if (!isDragging || !containerKanbanRef.current) return;

    const x = event.pageX - containerKanbanRef.current.offsetLeft;
    const walk = x - dragState.current.startX;

    containerKanbanRef.current.scrollLeft = dragState.current.scrollLeft - walk;
  }

  function handleMouseUp() {
    setIsDragging(false);
  }

  function handleMouseLeave() {
    setIsDragging(false);
  }

  function isCardElement(target) {
    return (
      target.classList.contains('kanban-card') ||
      target.closest('[data-rbd-draggable-id]') ||
      target.closest('[data-rbd-drag-handle-draggable-id]') ||
      target.closest('.kanban-card')
    );
  }

  function handleTouchStart(event) {
    if (isCardElement(event.target)) return;

    setIsDragging(true);
    dragState.current.startX =
      event.touches[0].pageX - containerKanbanRef.current.offsetLeft;
    dragState.current.scrollLeft = containerKanbanRef.current.scrollLeft;

    // Previne o comportamento padrão apenas se não for um card
    event.preventDefault();
  }

  function handleTouchMove(event) {
    if (!isDragging || !containerKanbanRef.current) return;

    const x = event.touches[0].pageX - containerKanbanRef.current.offsetLeft;
    const walk = x - dragState.current.startX;
    containerKanbanRef.current.scrollLeft = dragState.current.scrollLeft - walk;

    event.preventDefault();
  }

  function handleTouchEnd() {
    setIsDragging(false);
  }

  const handleScrollbarMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const track = scrollbarTrackRef.current;
    const thumb = scrollbarThumbRef.current;
    const container = containerKanbanRef.current;
    if (!track || !thumb || !container) return;

    const trackRect = track.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    let startScrollLeft = container.scrollLeft;

    if (!(e.clientX >= thumbRect.left && e.clientX <= thumbRect.right)) {
      const thumbWidth = thumb.offsetWidth;
      const clickPos = e.clientX - trackRect.left;
      const newThumbLeft = Math.max(0, Math.min(clickPos - thumbWidth / 2, trackRect.width - thumbWidth));
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const maxThumbLeft = trackRect.width - thumbWidth;
      container.scrollLeft = maxThumbLeft > 0 ? (newThumbLeft / maxThumbLeft) * maxScrollLeft : 0;
      startScrollLeft = container.scrollLeft;
    }

    scrollbarDragRef.current = { isDragging: true, startX: e.clientX, startScrollLeft };
    thumb.classList.add('dragging');

    const onMouseMove = (moveEvent) => {
      if (!scrollbarDragRef.current.isDragging) return;
      const deltaX = moveEvent.clientX - scrollbarDragRef.current.startX;
      const trackW = track.clientWidth;
      const thumbW = thumb.offsetWidth;
      const maxThumbL = trackW - thumbW;
      const maxScrollL = container.scrollWidth - container.clientWidth;
      const scrollDelta = maxThumbL > 0 ? (deltaX / maxThumbL) * maxScrollL : 0;
      container.scrollLeft = scrollbarDragRef.current.startScrollLeft + scrollDelta;
      updateScrollbar();
    };

    const onMouseUp = () => {
      scrollbarDragRef.current.isDragging = false;
      thumb.classList.remove('dragging');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      scrollbarCleanupRef.current = null;
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    scrollbarCleanupRef.current = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleScrollbarTouchStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const track = scrollbarTrackRef.current;
    const thumb = scrollbarThumbRef.current;
    const container = containerKanbanRef.current;
    if (!track || !thumb || !container) return;

    const touch = e.touches[0];
    const trackRect = track.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    let startScrollLeft = container.scrollLeft;

    if (!(touch.clientX >= thumbRect.left && touch.clientX <= thumbRect.right)) {
      const thumbWidth = thumb.offsetWidth;
      const clickPos = touch.clientX - trackRect.left;
      const newThumbLeft = Math.max(0, Math.min(clickPos - thumbWidth / 2, trackRect.width - thumbWidth));
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const maxThumbLeft = trackRect.width - thumbWidth;
      container.scrollLeft = maxThumbLeft > 0 ? (newThumbLeft / maxThumbLeft) * maxScrollLeft : 0;
      startScrollLeft = container.scrollLeft;
    }

    scrollbarDragRef.current = { isDragging: true, startX: touch.clientX, startScrollLeft };
    thumb.classList.add('dragging');

    const onTouchMove = (moveEvent) => {
      if (!scrollbarDragRef.current.isDragging) return;
      const t = moveEvent.touches[0];
      const deltaX = t.clientX - scrollbarDragRef.current.startX;
      const trackW = track.clientWidth;
      const thumbW = thumb.offsetWidth;
      const maxThumbL = trackW - thumbW;
      const maxScrollL = container.scrollWidth - container.clientWidth;
      const scrollDelta = maxThumbL > 0 ? (deltaX / maxThumbL) * maxScrollL : 0;
      container.scrollLeft = scrollbarDragRef.current.startScrollLeft + scrollDelta;
      updateScrollbar();
    };

    const onTouchEnd = () => {
      scrollbarDragRef.current.isDragging = false;
      thumb.classList.remove('dragging');
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      scrollbarCleanupRef.current = null;
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    scrollbarCleanupRef.current = () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <>
      <div className="m-1 d-flex justify-content-between align-items-center">
        <span
          style={{
            fontFamily: "'Open Sans', Arial, sans-serif",
          }}
        >
          Exibindo{' '}
          <span
            style={{
              fontFamily: "'Open Sans', Arial, sans-serif",
            }}
          >
            {quantidadeProspectosVisiveis()}
          </span>{' '}
          resultados
          {quantidadeProspectosOcultos() > 0 && (
            <small style={{ color: '#7b8190', marginLeft: 6 }}>
              ({quantidadeProspectosOcultos()} ocultos)
            </small>
          )}
        </span>
      </div>

      <DragDropContext onDragEnd={handleChangeEtapa}>
        <div
          ref={containerKanbanRef}
          className="kanban-scroll-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            width: `${windowWidth * 0.99}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: isDragging ? 'none' : 'auto',
            touchAction: 'pan-y pinch-zoom',
          }}
        >
          <div style={{ display: 'flex', minWidth: 'fit-content' }}>
            {columns &&
              columns.length > 0 &&
              columns.map(column => (
                <Droppable
                  key={column.codigo}
                  droppableId={column.codigo.toString()}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        paddingTop: 16,
                        padding: 3,
                        minWidth: 300,
                      }}
                    >
                      <div
                        className={`d-flex align-items-center justify-content-between mb-3 open-sans ${
                          column.codigo.toString() === maiorColuna
                            ? ' focar-coluna-kanban'
                            : ''
                        }`}
                        style={{
                          borderRadius: '8px',
                          minWidth: 0,
                          width: 300,
                          maxWidth: 300,
                          background: '#f8f8f8',
                          border: '1.5px solid #E5E6E7',
                          boxSizing: 'border-box',
                          height: 42,
                          padding: '0 16px',
                        }}
                      >
                        <span
                          style={{
                            textAlign: 'left',
                            fontWeight: 'semibold',
                            color: '#293C66',
                            fontSize: '1rem',
                          }}
                          className="open-sans"
                        >
                          {column.descricao}{' '}
                          {cards[column.codigo] && (
                            <small style={{ color: '#7b8190' }}>
                              ({cards[column.codigo].length}
                              {column.total
                                ? ` de ${column.total.TotalEtapa} `
                                : ''}
                              )
                            </small>
                          )}
                        </span>
                        <div className="d-flex align-items-center">
                          {onToggleOrdenacao && (
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              style={{
                                color: '#293C66',
                                fontSize: 18,
                                marginRight: 4,
                              }}
                              title="Ordenar por data da última ocorrência"
                              tabIndex={0}
                              onClick={() => {
                                if (onToggleOrdenacao) {
                                  onToggleOrdenacao(column.codigo);
                                }
                              }}
                            >
                              {ordenacaoColunas &&
                              ordenacaoColunas[column.codigo] === 'desc' ? (
                                <CaretDown size={18} />
                              ) : (
                                <CaretUp size={18} />
                              )}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            style={{ color: '#293C66', fontSize: 18 }}
                            title="Incluir mais cards"
                            tabIndex={-1}
                            disabled={disableOnSearch || loadingEtapa}
                          >
                            {acrescentarCards && (
                              <ArrowsClockwise
                                size={20}
                                onClick={() => {
                                  handleAcrescentarCards(column);
                                }}
                              />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="outer-container">
                        <div
                          className="div-scroll-kanban "
                          style={{
                            backgroundColor: '#f2f2f2',
                            height: `${windowHeight * heightColunaKanban -
                              25}px`,
                          }}
                        >
                          {(loadingEtapa || '') === column.descricao && (
                            <div
                              style={{
                                marginTop: '8px',
                                marginBottom: '8px',
                                color: '#aaa',
                                textAlign: 'center',
                              }}
                            >
                              <Loading
                                label={`Buscando Etapa ${loadingEtapa}`}
                              />
                            </div>
                          )}
                          {cards &&
                          cards[column.codigo] &&
                          cards[column.codigo].length > 0
                            ? cards[column.codigo].map((card, idx) => (
                                <Draggable
                                  key={card.codigo}
                                  draggableId={card.codigo.toString()}
                                  index={idx}
                                  isDragDisabled={isDragDisabled(card)}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      className="kanban-card"
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        userSelect: 'none',
                                        margin: '3px',
                                        ...provided.draggableProps.style,
                                        boxShadow: snapshot.isDragging
                                          ? '0 2px 8px rgba(0,0,0,0.18)'
                                          : 'none',
                                      }}
                                    >
                                      {renderCard({ info: card })}
                                    </div>
                                  )}
                                </Draggable>
                              ))
                            : loadingEtapa !== column.descricao && (
                                <div
                                  style={{
                                    marginTop: '8px',
                                    color: '#aaa',
                                    textAlign: 'center',
                                  }}
                                >
                                  Sem cards
                                </div>
                              )}
                          {provided.placeholder}
                        </div>
                      </div>
                    </div>
                  )}
                </Droppable>
              ))}
          </div>
        </div>
      </DragDropContext>

      {/* Custom horizontal scrollbar - Trello style */}
      <div
        ref={scrollbarTrackRef}
        className="kanban-custom-scrollbar-track"
        onMouseDown={handleScrollbarMouseDown}
        onTouchStart={handleScrollbarTouchStart}
        style={{ display: 'none' }}
      >
        <div
          ref={scrollbarThumbRef}
          className="kanban-custom-scrollbar-thumb"
        />
      </div>

      <Modal
        centered
        show={modalDescarte.openModal}
        onHide={() => {
          setModalDescarte({ openModal: false, result: null, infos: '' });
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Descarte</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="w-100 px-1 py-2">
            <Form.Group>
              <Form.Label>Escolha um motivo para realizar o descarte</Form.Label>
              <Form.Control
                as="select"
                name="CodigoMotivo"
                value={motivoDescarte}
                onChange={e => setMotivoDescarte(e.target.value)}
              >
                <option value="">Selecione um motivo</option>
                {options.motivosDescarte &&
                  options.motivosDescarte.map((item, index) => (
                    <option key={index} value={item.codigo}>
                      {item.descricao}
                    </option>
                  ))}
              </Form.Control>
              <Form.Text className="text-muted">Justificativa</Form.Text>
              <Form.Control
                type="text"
                className="mt-2"
                value={dissertativaDescarte}
                onChange={e => setDissertativaDescarte(e.target.value)}
                placeholder="Justificativa (opcional)"
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <button
              className="btn btn-secondary"
              onClick={() =>
                setModalDescarte({
                  openModal: false,
                  result: null,
                  infos: '',
                })
              }
            >
              Cancelar
            </button>
          </div>
          <div>
            <Button
              onClick={() => {
                handleModalDescarte();
              }}
              type="button"
              variant={'outline-primary'}
              className="active"
              style={{
                background: '#419AEF',
                borderColor: '#419AEF',
                fontFamily: 'Open Sans, Arial, sans-serif',
              }}
            >
              Confirmar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default KanbanBoard;
